import shortid from "shortid";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CardHeader,
  Chip,
  DataGrid,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Loading,
  TextField,
  Typography,
} from "../../../../../components";
import {
  OrcamentoServicosService,
  OrcamentoServicosItensService,
  OrcamentoServicosServicosService,
  ProdutosService,
} from "../../../../../services";
import { formatDate, formatPrice, responseErros } from "../../../../../utils";
import { calcularValorTotal } from "../../add-orcamento-servico";
import RenderizadorCamposValues from "../renderizador-campos-values.component";

const filtrosIniciasPaginacao = {
  page: 1,
  limit: 10,
  orderBy: "updatedAt",
  order: "desc",
};

const HistorioClienteDialog = ({
  open,
  setOpen,
  clienteId,
  orcamentosServicos,
  setOrcamentosServicos,
  totalItems,
  setTotalItems,
  prestadoresList,
  orcamentoServicoEntity,
  setOrcamentoServico,
  campoChave,
  setListaOrcamentoServicoProdutos,
  setListaOrcamentoServicoServicos,
}) => {
  const orcamentoServicoService = new OrcamentoServicosService();
  const orcamentoServicosItensService = new OrcamentoServicosItensService();
  const orcamentoServicosServicosService = new OrcamentoServicosServicosService();
  const produtosService = new ProdutosService();

  const [loading, setLoading] = useState(false);
  const [servicos, setServicos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [orcamentoSelecionado, setOrcamentoSelecionado] = useState(null);
  const [filtros, setFiltros] = useState({ ...filtrosIniciasPaginacao });
  const [query, setQuery] = useState("");

  const colunasServicos = [
    { field: "codigo", headerName: "Código", flex: 100 },
    { field: "descricao", headerName: "Serviço", flex: 500 },
    {
      field: "prestadorServicoId",
      headerName: "P. de Servico",
      flex: 150,
      sortable: false,
      valueGetter: (params) =>
        prestadoresList?.find((item) => item.id == params.value)?.nomeRazaoSocial,
    },
    { field: "quantidade", headerName: "Qtd.", flex: 90, headerAlign: "center", align: "center" },
    {
      field: "valor",
      headerName: "Valor (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => formatPrice(parseFloat(params.value || 0)),
    },
    {
      field: "totalItem",
      headerName: "Total",
      flex: 180,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        formatPrice(
          parseFloat(
            params.row.totalItem
              ? params.row.totalItem
              : params.row.valor * params.row.quantidade - (params.row.desconto || 0),
          ),
        ),
    },
  ];

  const colunasProdutos = [
    { field: "codigo", headerName: "Cod.", flex: 100 },
    { field: "descricao", headerName: "Produto", flex: 400 },
    { field: "quantidade", headerName: "Quantidade", headerAlign: "center", align: "center", width: 150 },
    {
      field: "valorUnitario",
      headerName: "Unitário R$",
      headerAlign: "right",
      align: "right",
      flex: 200,
      valueGetter: (params) => formatPrice(parseFloat(params.value), false),
    },
    {
      field: "totalItem",
      headerName: "Total R$",
      flex: 300,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        params.value ? formatPrice(parseFloat(params.value), false) : "",
    },
  ];

  useEffect(() => {
    if (open && clienteId && orcamentosServicos?.length === 0) {
      buscarOrcamentos({ ...filtros, clienteId });
    }
  }, [open, clienteId]);

  const buscarOrcamentos = async (filtrosBusca) => {
    setLoading(true);
    const result = await orcamentoServicoService.getAll(filtrosBusca);
    if (!result.isAxiosError) {
      setOrcamentosServicos(result.data.rows);
      setTotalItems(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarProdutosAvancado = (itens) => {
    const produtosIds = itens.map(({ produtoId }) => produtoId);
    produtosService
      .getAllFiltroAvancado({ id: produtosIds, ativado: true, nonPaginated: true })
      .then((result) => {
        if (!result.isAxiosError) {
          const produtosPreenchidos = itens.map((item) => {
            const prod = result.data.find((p) => p.id == item.produtoId);
            return { ...item, descricao: prod?.descricao, totalItem: calcularValorTotal(item) };
          });
          setProdutos(produtosPreenchidos);
        } else {
          responseErros(result);
        }
        setLoading(false);
      });
  };

  const handlePageChange = (newPage) => {
    const novosFiltros = { ...filtros, page: newPage };
    setFiltros(novosFiltros);
    buscarOrcamentos({ ...novosFiltros, clienteId });
    setOrcamentoSelecionado(null);
    setServicos([]);
    setProdutos([]);
  };

  const handleBusca = (e) => {
    if (e) e.preventDefault();
    const novosFiltros = { ...filtrosIniciasPaginacao };
    if (campoChave) {
      novosFiltros.campoChave = query;
    } else {
      novosFiltros.numeroDocumento = query;
    }
    setFiltros(novosFiltros);
    buscarOrcamentos({ ...novosFiltros, clienteId });
    setOrcamentoSelecionado(null);
    setServicos([]);
    setProdutos([]);
  };

  const handleOrcamentoClick = async (orcamento) => {
    setOrcamentoSelecionado(orcamento);
    setLoading(true);
    try {
      const resServicos = await orcamentoServicosServicosService.getAll({
        orcamentoServicoId: orcamento.id,
        nonPaginated: true,
      });
      if (!resServicos.isAxiosError) {
        setServicos(resServicos.data);
      } else {
        responseErros(resServicos);
        setServicos([]);
      }
      const resItens = await orcamentoServicosItensService.getAll({
        orcamentoServicoId: orcamento.id,
        nonPaginated: true,
      });
      if (!resItens.isAxiosError) {
        if (resItens.data?.length > 0) {
          buscarProdutosAvancado(resItens.data);
        } else {
          setProdutos([]);
          setLoading(false);
        }
      } else {
        responseErros(resItens);
        setProdutos([]);
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setServicos([]);
    setProdutos([]);
    setOrcamentoSelecionado(null);
  };

  const copiarOrcamento = async (orcamentoResumido) => {
    setLoading(true);
    try {
      const response = await orcamentoServicoService.getById(orcamentoResumido?.id);
      if (!response.isAxiosError && response.data) {
        const dados = response.data;
        orcamentoServicoEntity.insertDadosOrcamentoServico(dados, dados.id, true);
        if (dados.servicos?.length > 0) {
          const servicosCopia = dados.servicos.map((item) => ({
            ...item,
            id: shortid.generate(),
          }));
          orcamentoServicoEntity.setChangeServicos(servicosCopia);
          if (setListaOrcamentoServicoServicos) setListaOrcamentoServicoServicos(servicosCopia);
        } else {
          orcamentoServicoEntity.setChangeServicos([]);
          if (setListaOrcamentoServicoServicos) setListaOrcamentoServicoServicos([]);
        }
        if (dados.itens?.length > 0) {
          const itensCopia = dados.itens.map((item) => ({
            ...item,
            id: shortid.generate(),
            totalItem: item.quantidade * item.valorUnitario,
          }));
          orcamentoServicoEntity.setChangeItens(itensCopia);
          if (setListaOrcamentoServicoProdutos) setListaOrcamentoServicoProdutos(itensCopia);
        } else {
          orcamentoServicoEntity.setChangeItens([]);
          if (setListaOrcamentoServicoProdutos) setListaOrcamentoServicoProdutos([]);
        }
        setOrcamentoServico({ ...orcamentoServicoEntity });
        setOpen(false);
      } else {
        responseErros(response);
      }
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  };

  const abrirOrcamento = (idOrcamento) => {
    const novaUrl = window.location.pathname.replace(/create$/, idOrcamento);
    window.open(novaUrl, "_blank");
  };

  const totalPaginas = Math.ceil(totalItems / filtros.limit);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { minHeight: "75vh", maxHeight: "90vh" } }}
    >
      <div className="px-3">
        <CardHeader
          title="Histórico de Orçamentos de Serviço"
          action={
            <IconButton
              color="inherit"
              onClick={handleClose}
              sx={{ backgroundColor: "#EDEEF0", "&:hover": { backgroundColor: "#B6BABC" } }}
            >
              <i className="ph-bold ph-x" style={{ fontSize: "clamp(17px, 4vw, 20px)" }}></i>
            </IconButton>
          }
        />
      </div>
      <DialogContent sx={{ p: 1 }}>
        <Grid container sx={{ minHeight: "60vh" }}>
          <Grid
            item xs={12} md={4}
            sx={{ borderRight: "1px solid", borderColor: "divider", p: 0 }}
          >
            <Box sx={{ px: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1, mb: 3 }}>
                Lista de Orçamentos
              </Typography>
              <Box
                component="form"
                onSubmit={handleBusca}
                sx={{ display: "flex", alignItems: "stretch", gap: 1, mb: 3, height: "40px" }}
              >
                <Box sx={{ flex: 1, height: "100%" }}>
                  {campoChave ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        "& .MuiGrid-root": { m: 0, width: "100%", height: "100%" },
                        "& .MuiGrid-item": { p: 0, maxWidth: "100%", flexBasis: "100%" },
                        "& .MuiFormControl-root": { mb: 0, height: "100%" },
                        "& .MuiInputBase-root": { height: "100%" },
                      }}
                    >
                      <RenderizadorCamposValues
                        campos={[{ ...campoChave, value: query, label: `Pesquisar por ${campoChave?.label || "campo chave"}` }]}
                        onChangeValor={(id, val) => setQuery(val)}
                      />
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      label="Pesquisar por Nº Documento"
                      variant="outlined"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      sx={{ height: "100%", "& .MuiInputBase-root": { height: "100%" } }}
                    />
                  )}
                </Box>
                <Button type="submit" variant="contained" sx={{ height: "50px", minWidth: "100px" }}>
                  Buscar
                </Button>
              </Box>

              {orcamentosServicos?.length > 0 ? (
                <List sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                  {orcamentosServicos.map((orc) => (
                    <ListItemButton
                      key={orc.id}
                      sx={{
                        borderRadius: "8px",
                        mb: 2,
                        py: 1,
                        px: 2,
                        border: orcamentoSelecionado?.id === orc.id ? "2px solid #2196f3" : "1px solid transparent",
                        backgroundColor: orcamentoSelecionado?.id === orc.id ? "#e3f2fd" : "white",
                        boxShadow: orcamentoSelecionado?.id === orc.id
                          ? "0 4px 12px rgba(25, 118, 210, 0.15)"
                          : "0 2px 4px rgba(0, 0, 0, 0.05)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor: orcamentoSelecionado?.id === orc.id ? "#e3f2fd" : "#f5f5f5",
                        },
                      }}
                      disableRipple
                      selected={orcamentoSelecionado?.id === orc.id}
                      onClick={() => handleOrcamentoClick(orc)}
                    >
                      <ListItemText
                        primary={`Documento: ${orc?.numeroDocumento || ""}`}
                        secondary={`Data: ${format(formatDate.received(orc.createdAt), "dd/MM/yyyy")}`}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                          onClick={(e) => { e.stopPropagation(); abrirOrcamento(orc?.id); }}
                          size="small"
                        >
                          <i className="ph-fill ph-arrow-square-out" style={{ fontSize: 20 }}></i>
                        </IconButton>
                        <IconButton
                          onClick={(e) => { e.stopPropagation(); copiarOrcamento(orc); }}
                          size="small"
                        >
                          <i className="ph-fill ph-copy" style={{ fontSize: 20 }}></i>
                        </IconButton>
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "200px" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                    Nenhum orçamento encontrado.
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={8} sx={{ py: 1, px: 2, display: "flex", flexDirection: "column" }}>
            {orcamentoSelecionado ? (
              <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflowY: "auto", pr: 1 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    {`Detalhes do Orçamento - Documento: ${orcamentoSelecionado?.numeroDocumento || ""}`}
                  </Typography>
                </Box>
                {servicos.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Serviços</Typography>
                    <DataGrid
                      rows={servicos}
                      columns={colunasServicos}
                      loading={false}
                      disableSelectionOnClick
                      noFilter
                      disableAcoes
                      hideFooter
                      autoHeight
                    />
                  </Box>
                )}
                {produtos.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Produtos</Typography>
                    <DataGrid
                      rows={produtos}
                      columns={colunasProdutos}
                      loading={loading}
                      disableSelectionOnClick
                      noFilter
                      disableAcoes
                      hideFooter
                      autoHeight
                    />
                  </Box>
                )}
                {servicos.length === 0 && produtos.length === 0 && !loading && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
                    Nenhum serviço ou produto neste orçamento.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4 }}>
                <Box sx={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
                  <i className="ph ph-arrow-left" style={{ fontSize: 36, color: "#bdbdbd" }} />
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 500, textAlign: "center" }}>
                  Selecione um Orçamento
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 280 }}>
                  Clique em um orçamento na lista ao lado para visualizar seus serviços e produtos.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="d-flex justify-content-start m-2">
        <Box className="d-flex justify-content-center align-items-center gap-2" sx={{ px: "2.5rem" }}>
          <Button onClick={() => handlePageChange(filtros.page - 1)} disabled={filtros.page === 1}>
            Anterior
          </Button>
          <Chip label={`Página ${filtros.page} de ${totalPaginas === 0 ? 1 : totalPaginas}`} />
          <Button
            onClick={() => handlePageChange(filtros.page + 1)}
            disabled={filtros.page === totalPaginas || totalPaginas === 0}
          >
            Próxima
          </Button>
        </Box>
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
};

export default HistorioClienteDialog;
